import { useRef } from "react"

import { Box, Stack, Tooltip } from "@mui/material"
import { useQueryAdminDashboardTeachers } from "api/reactQuery/queries/adminDashboard"
import NoMatchesForSearch from "components/common/listing/noMatchesForSearch"
import Pagination from "components/common/pagination"
import SearchbarForm from "components/form/searchbarForm"
import { useTranslation } from "react-i18next"
import { visuallyHiddenSx } from "styles/common/typography"
import {
  IDashboardPeriodParams,
  IDashboardTeacherRow,
  IDashboardTeachersParams,
} from "ts/interfaces/AdminDashboard"
import { DashboardTeacherSortKey } from "ts/types/AdminDashboard"

import { Amount, Dash } from "./DashboardCells"
import { formatLastActive, getCountTrend } from "./dashboardFormat"
import DashboardSection from "./DashboardSection"
import DashboardSectionBody, { useShownValue } from "./DashboardSectionBody"
import DashboardTable, { IDashboardTableColumn } from "./DashboardTable"
import PositivityChip from "./PositivityChip"
import useDashboardTablePaging, {
  DASHBOARD_PAGE_SIZE,
} from "./useDashboardTablePaging"
import useDashboardTableState from "./useDashboardTableState"

const TEACHER_SORT_KEYS: DashboardTeacherSortKey[] = [
  "first_name",
  "last_name",
  "last_active",
  "positivity",
  "transaction_count",
  "bonus_amount",
  "fine_amount",
  "salary_amount",
  "expense_amount",
]

const formatName = (row: IDashboardTeacherRow) =>
  [row.first_name, row.last_name].filter(Boolean).join(" ") || "—"

type TrendType = "bonus" | "fine"

// Always takes its slot, so amounts line up.
const TrendArrow = ({
  type,
  count,
  prior,
}: {
  type: TrendType
  count: number | null
  prior: number | null
}) => {
  const { t } = useTranslation()
  const trend = getCountTrend(count, prior)
  const delta = Math.abs((count ?? 0) - (prior ?? 0))
  const direction = trend === "down" ? "Fewer" : "More"
  return (
    <Box component="span" sx={{ display: "inline-block", width: 16, ml: 0.5 }}>
      {trend && count !== null && (
        <Tooltip
          title={t(`adminDashboard.countTrend${direction}_${type}`, {
            count: delta,
            formatted: delta.toLocaleString("en-US"),
          })}
        >
          <Box
            component="span"
            role="img"
            tabIndex={0}
            sx={{
              color: "mockup.neutral30",
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "mockup.primary60",
              },
            }}
          >
            {trend === "down" ? "▼" : "▲"}
          </Box>
        </Tooltip>
      )}
    </Box>
  )
}

const AllTeachers = ({ params }: { params: IDashboardPeriodParams }) => {
  const { t } = useTranslation()
  const {
    search,
    sortBy,
    sortDir,
    page,
    setSearch,
    toggleSort,
    nameSortKey,
    nameSortSuffix,
    setPage,
  } = useDashboardTableState({ prefix: "te", sortKeys: TEACHER_SORT_KEYS })
  // BED 422s on an empty `search`.
  const table: IDashboardTeachersParams = {
    ...(search && { search }),
    sort_by: sortBy,
    sorting: sortDir,
    skip: (page - 1) * DASHBOARD_PAGE_SIZE,
    limit: DASHBOARD_PAGE_SIZE,
  }
  const query = useQueryAdminDashboardTeachers(params, table)
  const shownPage = useShownValue(page, query.isPreviousData)
  const shownSearch = useShownValue(search, query.isPreviousData)
  const data = query.data?.data
  const rows = data?.objects ?? []
  const total = data?.all_objects_count ?? null
  const showsTrends = !!data && data.prior_date_from !== null
  const from = (shownPage - 1) * DASHBOARD_PAGE_SIZE + 1
  const to = from + rows.length - 1
  // A stale link's page past the end isn't empty; it's clamped below.
  const isEmpty =
    !!data && rows.length === 0 && (total === 0 || shownPage === 1)
  const summary =
    total === null
      ? undefined
      : t("adminDashboard.showingTeachers", {
          from,
          to,
          total: total.toLocaleString("en-US"),
        })
  const emptyText = shownSearch
    ? t("setup.noMatchesFor", { match: shownSearch })
    : t("adminDashboard.noTeachersYet")

  const { pageCount, announcement, announceNextResult } =
    useDashboardTablePaging({
      query,
      page,
      shownPage,
      rowCount: rows.length,
      total,
      setPage,
      resultText: isEmpty ? emptyText : (summary ?? ""),
    })

  const regionRef = useRef<HTMLDivElement>(null)

  const handleSearch = (value: string) => {
    announceNextResult()
    setSearch(value)
  }

  const handleSort = (key: DashboardTeacherSortKey) => {
    announceNextResult()
    toggleSort(key)
  }

  const handlePageChange = (value: number) => {
    announceNextResult()
    setPage(value)
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches
    regionRef.current?.scrollIntoView({
      block: "start",
      behavior: reduceMotion ? "auto" : "smooth",
    })
    regionRef.current?.focus({ preventScroll: true })
  }

  const amount = (value: number) => <Amount value={value} />
  const withTrend = (
    value: number,
    type: TrendType,
    count: number | null,
    prior: number | null,
  ) => (
    <>
      <Amount value={value} />
      <TrendArrow
        type={type}
        count={showsTrends ? count : null}
        prior={prior}
      />
    </>
  )

  const columns: IDashboardTableColumn<
    IDashboardTeacherRow,
    DashboardTeacherSortKey
  >[] = [
    {
      id: "teacher",
      label: `${t("adminDashboard.column_teacher")}${nameSortSuffix}`,
      sortKey: nameSortKey,
      render: formatName,
    },
    {
      id: "positivity",
      label: t("adminDashboard.column_positivity"),
      sortKey: "positivity",
      render: (row) => <PositivityChip positivity={row.positivity} />,
    },
    {
      id: "total",
      label: t("adminDashboard.column_total"),
      sortKey: "transaction_count",
      align: "right",
      info: t("adminDashboard.totalInfo"),
      render: (row) => row.transaction_count.toLocaleString("en-US"),
    },
    {
      id: "bonus",
      label: t("adminDashboard.column_bonus"),
      sortKey: "bonus_amount",
      align: "right",
      render: (row) =>
        withTrend(
          row.bonus_amount,
          "bonus",
          row.bonus_count,
          row.prior_bonus_count,
        ),
    },
    {
      id: "fine",
      label: t("adminDashboard.column_fine"),
      sortKey: "fine_amount",
      align: "right",
      render: (row) =>
        withTrend(
          row.fine_amount,
          "fine",
          row.fine_count,
          row.prior_fine_count,
        ),
    },
    {
      id: "salary",
      label: t("adminDashboard.column_salary"),
      sortKey: "salary_amount",
      align: "right",
      render: (row) => amount(row.salary_amount),
    },
    {
      id: "expense",
      label: t("adminDashboard.column_expense"),
      sortKey: "expense_amount",
      align: "right",
      render: (row) => amount(row.expense_amount),
    },
    {
      id: "lastActive",
      label: t("adminDashboard.column_lastActive"),
      sortKey: "last_active",
      info: t("adminDashboard.lastActiveInfo"),
      render: (row) =>
        row.last_active ? (
          formatLastActive(row.last_active)
        ) : (
          <Dash label={t("adminDashboard.lastActiveNever")} />
        ),
    },
  ]

  const tableProps = {
    label: t("adminDashboard.allTeachersTable"),
    columns,
    getRowId: (row: IDashboardTeacherRow) => row.fk_teacher_id,
    sortBy,
    sortDir,
    onSort: handleSort,
  }

  return (
    <DashboardSection
      title={t("adminDashboard.allTeachers")}
      question={t("adminDashboard.allTeachersQuestion")}
      questionGap={1}
      period={query.data?.data}
    >
      <Stack rowGap={2}>
        <SearchbarForm
          initialValue={search}
          clearable
          placeholder={t("adminDashboard.searchTeacher")}
          onSubmit={(form) => handleSearch(form.search)}
        />
        <DashboardSectionBody
          queries={[query]}
          // Centred in the 16px gap
          progressTop={-9}
          isEmpty={isEmpty}
          emptyText={
            shownSearch ? (
              <NoMatchesForSearch search={shownSearch} />
            ) : (
              emptyText
            )
          }
          skeleton={
            <DashboardTable
              {...tableProps}
              rows={[]}
              skeletonRows={DASHBOARD_PAGE_SIZE}
            />
          }
        >
          <DashboardTable {...tableProps} rows={rows} regionRef={regionRef} />
        </DashboardSectionBody>
        {rows.length > 0 && (
          <Pagination
            page={page}
            count={pageCount}
            onChange={handlePageChange}
            summary={summary}
          />
        )}
      </Stack>
      <Box role="status" sx={visuallyHiddenSx}>
        {announcement}
      </Box>
    </DashboardSection>
  )
}

export default AllTeachers
